import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import DesktopTable from 'components/Common/DesktopTable';
import MobileTable from 'components/Common/MobileTable';
import Render from 'components/Common/Render';
import BusinessHeader from 'components/Home/BusinessHeader';
import { http } from 'middleware/apiMiddleware';
import { topUpHistory } from 'constants/apiUrls';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { RootState } from 'store';
import { settopUpHistory } from 'store/slices/topUpHistorySlice';
import GoBack from 'components/Common/GoBack';
import { ReactComponent as NoTransactionsImage } from 'assets/no_transactions.svg';

const TABLE_HEAD = ['Transaction ID', 'Amount', 'Date', 'Transaction type', 'Status'];

const TopupHistory = () => {
  const dispatch = useAppDispatch();
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const topUpHistoryState = useAppSelector((state: RootState) => state?.topUpHistory?.topUpHistory);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMoreData, sethasMoreData] = useState<boolean>(true);
  const [busienssUuid, setbusienssUuid] = useState<string | null>(null);
  const [dataLength, setdataLength] = useState<number>(0);
  const [limit] = useState<number>(20);
  const [offSet, setoffSet] = useState<number>(0);

  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const business: BusinessType = businessState?.business || selectedBusiness || null;

  const getHistory = useCallback(
    async (busienssUuid: string) => {
      try {
        setLoading(true);
        const { data, error } = await http.get(
          topUpHistory(busienssUuid, limit, offSet),
          undefined,
          true,
        );

        if (!!data && !error) {
          setdataLength(data?.count);
          let createdData = data?.results?.map((item: any) => {
            return {
              transactionID: item?.transactionId,
              amount: item?.transactionAmount,
              date: moment(item?.transactionDate).format('MMM DD, YYYY'),
              type: item?.transactionType,
              status: item?.transactionStatus,
            };
          });

          if (offSet > 0) {
            dispatch(settopUpHistory([...topUpHistoryState, ...createdData]));
          } else {
            dispatch(settopUpHistory([...createdData]));
          }

          setoffSet((prev) => prev + limit);
          if (data?.next) {
            sethasMoreData(true);
          } else {
            sethasMoreData(false);
          }
        }
      } catch {
        // Failed to fetch transaction history
      } finally {
        setLoading(false);
      }
    },
    [business?.uuid, limit, offSet],
  );

  useEffect(() => {
    if (business && business?.uuid) {
      setbusienssUuid(business?.uuid);
    }
  }, [business]);

  useEffect(() => {
    if (busienssUuid) {
      getHistory(busienssUuid);
    }
  }, [busienssUuid]);

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      <Render when={!!business}>
        <div className="pt-8 pb-2 px-6">
          <BusinessHeader selectedBusiness={business} />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden bg-offWhite min-h-0">
          <div className="px-8 pt-5 pb-4 flex-shrink-0">
            <GoBack label="Transaction History" containerClass="!my-0 text-xl font-semibold" />
          </div>
          <div className="flex-1 overflow-hidden px-8 pb-8 flex flex-col gap-4 min-h-0">
            {topUpHistoryState?.length <= 0 && !loading ? (
              <div className="flex flex-col items-center">
                <NoTransactionsImage width={'100%'} />
                No transactions just yet. Your transaction history is currently empty
              </div>
            ) : (
              <>
                <div className="md:block hidden">
                  <DesktopTable
                    historyDetailsList={topUpHistoryState}
                    headerList={TABLE_HEAD}
                    loading={loading}
                    fetchData={getHistory}
                    hasMoreData={hasMoreData}
                    businessID={business ? business.uuid : ''}
                  />
                </div>
                <div className="md:hidden">
                  <MobileTable
                    historyDetailsList={topUpHistoryState}
                    headerList={TABLE_HEAD}
                    loading={loading}
                    fetchData={getHistory}
                    hasMoreData={hasMoreData}
                    businessID={business ? business.uuid : ''}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Render>
    </div>
  );
};

export default TopupHistory;
